import React from "react";
import GraderWidget from "@/components/GraderWidget";
import AuthModal from "@/components/AuthModal";
import { ArrowRight, Wand2, LayoutGrid, Gauge, Check, Upload } from "lucide-react";

// Opens the email/password auth modal (kept the old name so every CTA keeps working).
function loginWithGoogle() {
  window.dispatchEvent(new CustomEvent("open-auth", { detail: { mode: "register" } }));
}

const platformLogos = [
  "Etsy","Amazon Merch","Redbubble","TeePublic","Shopify","Society6","Zazzle","Printful","Printify","Displate","Threadless","Gelato","Spring","Spreadshirt","Merchize","Fourthwall","Gumroad","Sellfy"
];

export default function Landing() {
  return (
    <div>
      <AuthModal />
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#09090B] text-white hero-noise">
        <nav className="relative z-10 max-w-7xl mx-auto flex items-center justify-between px-6 py-6">
          <a href="/" className="flex items-center gap-2" data-testid="brand-logo">
            <span className="inline-block w-8 h-8 rounded-lg bg-lime" />
            <span className="text-lg font-black h-tight tracking-tight">TagTitan</span>
          </a>
          <div className="hidden md:flex items-center gap-6 text-sm text-zinc-300">
            <a href="#features" className="hover:text-white">Features</a>
            <a href="#grader" className="hover:text-white">Free Grader</a>
            <a href="#pricing" className="hover:text-white">Pricing</a>
          </div>
          <button data-testid="nav-signin-btn" onClick={loginWithGoogle}
                  className="btn-primary text-sm">
            Sign in
          </button>
        </nav>

        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-10 pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.25em] text-lime border border-zinc-800 rounded-full px-3 py-1">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-lime" /> AI listings for print-on-demand
            </span>
            <h1 className="mt-6 text-5xl md:text-6xl lg:text-7xl font-black h-tight leading-[0.95]" data-testid="hero-headline">
              Upload your design.<br/>
              Get a <span className="text-lime">perfect</span>, keyword-ranked listing<br/>
              for every store. <span className="text-zinc-500">In seconds.</span>
            </h1>
            <p className="mt-6 text-lg text-zinc-400 max-w-2xl">
              TagTitan reads your artwork with vision AI and writes SEO titles, tags, descriptions and bullets for Etsy, Amazon Merch, Redbubble, Shopify and 14 more platforms — all at once.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button data-testid="hero-start-btn" onClick={loginWithGoogle} className="btn-primary inline-flex items-center gap-2">
                Start free <ArrowRight size={16} />
              </button>
              <a href="#grader" className="btn-dark inline-flex items-center gap-2 border border-zinc-800">
                Try the free grader
              </a>
            </div>
            <div className="mt-6 flex items-center gap-4 text-xs text-zinc-500">
              <span className="inline-flex items-center gap-1"><Check size={14} className="text-lime" /> 5 free generations</span>
              <span className="inline-flex items-center gap-1"><Check size={14} className="text-lime" /> No credit card</span>
              <span className="inline-flex items-center gap-1"><Check size={14} className="text-lime" /> Grader is 100% free</span>
            </div>
          </div>

          <div className="lg:col-span-5" id="grader">
            <GraderWidget dark />
          </div>
        </div>

        {/* platform marquee */}
        <div className="relative z-10 border-t border-zinc-900 py-6 overflow-hidden">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">One click. Every marketplace.</div>
            <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm text-zinc-400 font-semibold">
              {platformLogos.map(p => <span key={p} className="hover:text-lime transition-colors">{p}</span>)}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid md:grid-cols-3 gap-6">
          <FeatureCard
            icon={<Upload />}
            kicker="1 · Image to listing"
            title="Upload your design. We write the rest."
            body="Vision AI reads the subject, style, colors, mood and niche — then writes titles, tags, and bullets. Typing is optional."
          />
          <FeatureCard
            icon={<LayoutGrid />}
            kicker="2 · Every store at once"
            title="One button. 18 marketplaces."
            body="One Generate button produces properly formatted listings for Etsy (13 tags, 140 chars), Amazon Merch (60 chars), Redbubble, Shopify, and every store you sell on."
          />
          <FeatureCard
            icon={<Gauge />}
            kicker="3 · Free listing grader"
            title="Score your listing 0–100."
            body="Paste any Etsy or Amazon listing and get a specific fix list. Free forever. No signup. Your unfair advantage against every other seller."
          />
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="bg-zinc-50 border-y border-zinc-200 py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-2xl">
            <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-zinc-500">Pricing</div>
            <h2 className="mt-2 text-4xl md:text-5xl font-black h-tight">Start free. Pay when you're winning.</h2>
            <p className="mt-3 text-zinc-600">One credit per generation. "All platforms at once" is still one credit.</p>
          </div>
          <div className="mt-10 grid md:grid-cols-4 gap-5">
            <PriceCard tier="Free" price="$0" cta="Start free" onClick={loginWithGoogle} testId="price-free-btn"
              features={["5 generations", "All 18 platforms", "Vision + text input", "Free grader forever"]}
            />
            <PriceCard tier="Credit pack" price="$9" note="100 credits" cta="Get 100 credits" onClick={loginWithGoogle} testId="price-credits-btn"
              features={["100 full generations", "All platforms at once", "CSV bulk export", "Never expire"]}
            />
            <PriceCard tier="Pro" price="$19" note="/ month" cta="Go Pro" onClick={loginWithGoogle} highlight testId="price-pro-btn"
              features={["500 listings / month", "Bulk mode", "Brand voice memory", "Priority queue"]}
            />
            <PriceCard tier="Studio" price="$39" note="/ month" cta="Go Studio" onClick={loginWithGoogle} testId="price-studio-btn"
              features={["5,000 listings / month", "Everything in Pro", "Built for agencies", "Highest priority"]}
            />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#09090B] text-zinc-500 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-white">
            <span className="inline-block w-6 h-6 rounded-md bg-lime" />
            <span className="font-black">TagTitan</span>
          </div>
          <div className="text-xs">© {new Date().getFullYear()} TagTitan — Every marketplace, one button.</div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, kicker, title, body }) {
  return (
    <div className="tt-card p-7 hover:shadow-[0_20px_50px_rgb(0,0,0,0.08)] transition-shadow">
      <div className="w-11 h-11 rounded-xl bg-[#09090B] text-lime flex items-center justify-center">
        {icon}
      </div>
      <div className="mt-5 text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">{kicker}</div>
      <h3 className="mt-2 text-2xl font-extrabold h-tight">{title}</h3>
      <p className="mt-2 text-zinc-600 text-sm leading-relaxed">{body}</p>
    </div>
  );
}

function PriceCard({ tier, price, note, cta, features, highlight, onClick, testId }) {
  return (
    <div className={"tt-card p-7 flex flex-col " + (highlight ? "border-2 border-[#09090B] relative" : "")}>
      {highlight && <div className="absolute -top-3 left-6 bg-lime text-[10px] font-black uppercase tracking-[0.2em] px-2 py-1 rounded">Best value</div>}
      <div className="text-sm font-bold uppercase tracking-widest text-zinc-500">{tier}</div>
      <div className="mt-3 flex items-baseline gap-2">
        <div className="text-5xl font-black h-tight">{price}</div>
        {note && <div className="text-zinc-500 text-sm">{note}</div>}
      </div>
      <ul className="mt-5 space-y-2 flex-1">
        {features.map(f => (
          <li key={f} className="flex items-start gap-2 text-sm text-zinc-800"><Check size={16} className="text-[#10B981] mt-0.5" /> {f}</li>
        ))}
      </ul>
      <button data-testid={testId} onClick={onClick} className={"mt-7 " + (highlight ? "btn-primary" : "btn-dark") + " w-full inline-flex items-center justify-center gap-2"}>
        {cta}
      </button>
    </div>
  );
}
